import ms from 'ms';
import type { ObjectSchema } from 'yup';
import Yup from '@tmh-website/services/validation.service';

import type { Theme } from '../utils/types';
import { ETFormLayout, ETFormVariant, COUNTRY_CODE_TO_IBAN_PATTERN_MAP, SalutationFR, Step } from '../utils/const';
import { ReviewShared } from './shared';

export const fullAgeDate = new Date(Date.now() - ms('18years'));

/**
 * Keep validation in sync with server-side validation @ `apps/cms/src/api/electricity-tariff/validation.ts:22`
 */
export const PowerConsumption = Yup.object({
  chargePercentRate: Yup.number(),
  connectedTime: Yup.number().required(),
  consumptionByDistance: Yup.number(),
  consumptionPerYear: Yup.number(),
  distancePerYear: Yup.number(),
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  meterNumber: Yup.string()
    .trim()
    .matches(/^\d{14}$/, 'Votre numéro de PDL est composé exactement de 14 caractères numériques.')
    .required(),
  salutation: Yup.string().oneOf(Object.values(SalutationFR)).required(),
  selectedCar: Yup.string(),
  userHaveCar: Yup.string().oneOf(['yes', 'no']).required(),
});

// adjust validation based on need to select tariff
export const TariffInfo = Yup.object({});

// TODO adjust validation based on target api expected format (e.g. max length or similar)
export const UserData = Yup.object({
  email: Yup.string().email().required(),
  billingZipCode: Yup.mixed().when('differentBillingAddress', {
    is: true,
    then: Yup.string()
      .matches(/^\d{5}$/, 'Format invalide')
      .required(),
  }),
  billingCity: Yup.mixed().when('differentBillingAddress', {
    is: true,
    then: Yup.string().required(),
  }),
  billingStreet: Yup.mixed().when('differentBillingAddress', {
    is: true,
    then: Yup.string().required(),
  }),
  billingStreetNumber: Yup.mixed().when('differentBillingAddress', {
    is: true,
    then: Yup.string().required(),
  }),
  differentPersonalInfoData: Yup.boolean().required(),
  differentBillingAddress: Yup.boolean().required(),
  personId: Yup.string().required(),
  differentSalutation: Yup.string().when('differentPersonalInfoData', {
    is: true,
    then: Yup.string().required(),
  }),
  differentFirstName: Yup.mixed().when('differentPersonalInfoData', {
    is: true,
    then: Yup.string().required(),
  }),
  differentLastName: Yup.mixed().when('differentPersonalInfoData', {
    is: true,
    then: Yup.string().required(),
  }),
  iban: Yup.string()
    .test('is-valid-iban', 'Format IBAN invalide', function (value) {
      if (!value) return false;

      const countryCode = value.slice(0, 2).toUpperCase();

      const pattern = COUNTRY_CODE_TO_IBAN_PATTERN_MAP[countryCode];

      if (!pattern) {
        return this.createError({ message: `Le format IBAN pour le pays ${countryCode} n'est pas pris en charge` });
      }

      return pattern.test(value);
    })
    .required("L'IBAN est requis"),

  accountHolderName: Yup.string().required(),
  differentAccountHolder: Yup.bool(),
  phone: Yup.string()
    .matches(/^\d{9}$/, 'Format invalide')
    .required(),
});

export const Review = Yup.object({
  ...ReviewShared.fields,
});

export const Thanks = Yup.object({
  validationCode: Yup.string()
    .matches(/^\d{4}$/, 'Format invalide')
    .required(),
});

export interface ValuesFR {
  _variant: ETFormVariant.RenaultFR;
  _layout: ETFormLayout.FR;
  [Step.PowerConsumption]: Yup.InferType<typeof PowerConsumption>;
  [Step.TariffInfo]: Yup.InferType<typeof TariffInfo>;
  [Step.UserData]: Yup.InferType<typeof UserData>;
  [Step.Review]: Yup.InferType<typeof Review>;
  [Step.Thanks]: Yup.InferType<typeof Thanks>;
  [key: string]: unknown;
}

export const initialValues: ValuesFR = {
  _variant: ETFormVariant.RenaultFR,
  _layout: ETFormLayout.FR,
  [Step.PowerConsumption]: {
    chargePercentRate: 90,
    connectedTime: 15,
    consumptionByDistance: 0,
    consumptionPerYear: 0,
    distancePerYear: 10_000,
    firstName: '',
    lastName: '',
    meterNumber: '',
    salutation: '',
    selectedCar: '',
    userHaveCar: 'yes',
  },
  [Step.TariffInfo]: {},
  [Step.UserData]: {
    email: '',
    differentBillingAddress: false,
    billingZipCode: '',
    billingCity: '',
    billingStreet: '',
    billingStreetNumber: '',
    differentPersonalInfoData: false,
    differentSalutation: '',
    differentFirstName: '',
    differentLastName: '',
    personId: '',
    phone: '',
    iban: '',
    accountHolderName: '',
    differentAccountHolder: false,
  },
  [Step.Review]: {
    acceptAdvertising: false,
  },
  [Step.Thanks]: {
    validationCode: '',
  },
};

export function getInitialValues(theme: Theme): ValuesFR {
  (theme.reviewCheckboxEntries ?? []).forEach(entry => {
    initialValues[Step.Review][entry.name] = false;
  });
  return initialValues;
}

export function getValidations(
  theme: Theme,
  _options: {}
): {
  [key in Step]: ObjectSchema<any>;
} {
  const customReview = {};

  (theme.reviewCheckboxEntries ?? []).forEach(entry => {
    let validation = Yup.boolean();
    if (entry.required) {
      validation = validation.isTrue().required();
    }
    customReview[entry.name] = validation;
  });

  return {
    [Step.PowerConsumption]: PowerConsumption,
    [Step.TariffInfo]: TariffInfo,
    [Step.UserData]: UserData,
    [Step.Review]: Review.concat(Yup.object(customReview)),
    [Step.Thanks]: Thanks,
  };
}
